import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { DietaryRestriction, FoodPreference, PreferenceSubject, TasteVector } from "@/shared/contracts";
import { newIdempotencyKey } from "@/shared/api/idempotency";
import { env } from "@/shared/config/env";
import { ApiError, toApiError } from "@/shared/api/errors";
import { confirmCurrentFoodPreferenceReceipt, validOriginalFoodPreferenceResult, foodPreferenceScopeKey, readFoodPreferenceReceipt, reserveFoodPreferenceReceipt, updateFoodPreferenceReceipt, type FoodPreferenceScope } from "@/shared/query/foodPreferenceReceipt";
import { useOptionalCurrentHousehold } from "@/shared/session/householdContext";
import { useServices } from "@/shared/services/registry";
import { Button, Card, Chip, DataOriginBadge, ErrorState, Icon, ToggleChip } from "@/shared/ui";

const DIMENSIONS: Array<{ key: keyof TasteVector; label: string }> = [
  { key: "salty", label: "咸" },
  { key: "spicy", label: "辣" },
  { key: "oily", label: "油" },
  { key: "rich_broth", label: "浓汤" },
  { key: "sweet", label: "甜" },
];
const LEVELS: Array<{ value: number | null; label: string }> = [
  { value: -2, label: "很少" },
  { value: -1, label: "少一点" },
  { value: null, label: "随意" },
  { value: 1, label: "多一点" },
  { value: 2, label: "很爱" },
];

/**
 * 两套偏好分开编辑：宠物的旅途口味（它自己的性格）与主人的现实用餐偏好（含私密饮食限制）。
 * 饮食限制恒为私密，只用于给主人推荐。保存后版本 +1，已有推荐按新版本重新请求。
 */
export function PreferenceEditor({ petId, subject, variant }: { petId: string; subject: PreferenceSubject; variant?: string }) {
  const context = useOptionalCurrentHousehold();
  const fixture = env.dataMode === "fixture";
  const authorized = Boolean(context?.userId && context.household?.household_id && context.pet?.pet_id === petId
    && context.household.pets.some(pet => pet.pet_id === petId && pet.join_step === "moved_in"));
  const scope: FoodPreferenceScope | null = (fixture ? Boolean(petId.trim()) && (!context?.pet || context.pet.pet_id === petId) : authorized) ? {
    mode: fixture ? "fixture" : "live", userId: context?.userId ?? "fixture", householdId: context?.household?.household_id ?? "fixture", petId, subject,
  } : null;
  const fingerprint = scope ? foodPreferenceScopeKey(scope) + JSON.stringify(variant ?? null) : "unavailable";
  const authority = useRef({ fingerprint, epoch: 0, active: true });
  if (authority.current.fingerprint !== fingerprint) authority.current = { fingerprint, epoch: authority.current.epoch + 1, active: true };
  const epoch = authority.current.epoch;
  useLayoutEffect(() => { authority.current.active = true; return () => { authority.current.active = false; }; }, []);
  const isCurrent = () => authority.current.active && authority.current.fingerprint === fingerprint && authority.current.epoch === epoch;
  if (!scope) return <Card flat><p role="alert">现在无法确认这份偏好属于谁，请返回确认当前家庭和伙伴。</p></Card>;
  return <ScopedPreferenceEditor key={fingerprint} scope={scope} variant={variant} isCurrent={isCurrent} />;
}

function ScopedPreferenceEditor({ scope, variant, isCurrent }: { scope: FoodPreferenceScope; variant?: string; isCurrent: () => boolean }) {
  const { petId, subject } = scope;
  const { food } = useServices();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [storageFailure, setStorageFailure] = useState(false);
  const [, refreshReceipt] = useState(0);
  const active = useRef(true);
  useLayoutEffect(() => { active.current = true; return () => { active.current = false; }; }, []);
  const current = () => active.current && isCurrent();
  const key = ["food", "preference-scoped", scope.mode, scope.userId, scope.householdId, petId, subject, variant ?? "-"];
  const matches = (value: FoodPreference) => value.pet_id === petId && value.subject === subject && Number.isSafeInteger(value.version) && value.version >= 0
    && (scope.mode !== "fixture" || value.source === "fixture");
  const changed = () => new ApiError({ kind: "http", status: 409, code: "VERSION_CONFLICT", retryable: false, message: "这份偏好的归属已经变化，请返回重新查看。" });
  const pref = useQuery({ queryKey: key, queryFn: async ({ signal }) => {
    if (!current()) throw changed();
    const value = await food.preference(petId, subject, variant);
    if (!current() || signal.aborted || !matches(value)) throw changed();
    return value;
  }, retry: false, refetchOnMount: false, refetchOnWindowFocus: false, refetchOnReconnect: false });
  const [draft, setDraft] = useState<FoodPreference | null>(null);
  const [restrictionText, setRestrictionText] = useState("");
  const receipt = readFoodPreferenceReceipt(scope);
  const held = receipt.kind === "held" && receipt.receipt.state !== "confirmed";
  const blocked = storageFailure || receipt.kind === "error" || held;
  useEffect(() => {
    if (pref.data && matches(pref.data) && !open && current()) {
      setDraft(pref.data);
      setRestrictionText(pref.data.restrictions.map((r) => r.label).join("、"));
    }
  }, [pref.data, open]);
  type Submission = { next: FoodPreference; idempotencyKey: string; expectedVersion: number };
  const save = useMutation({
    mutationKey: [...key, "save"], retry: false, networkMode: "always",
    mutationFn: ({ next, idempotencyKey, expectedVersion }: Submission) => {
      const saved = readFoodPreferenceReceipt(scope);
      if (!current() || !matches(next) || saved.kind !== "held" || saved.receipt.state !== "pending" || saved.receipt.idempotencyKey !== idempotencyKey || saved.receipt.expectedVersion !== expectedVersion) throw changed();
      return food.savePreference(subject, next, idempotencyKey);
    },
    onSuccess: (saved, submission) => {
      if (!matches(saved) || saved.preference_id !== submission.next.preference_id || saved.version <= submission.expectedVersion) {
        const stopped = updateFoodPreferenceReceipt(scope, submission.idempotencyKey, "blocked");
        if (current()) { if (!stopped) setStorageFailure(true); refreshReceipt(n => n + 1); }
        return;
      }
      const confirmed = updateFoodPreferenceReceipt(scope, submission.idempotencyKey, "confirmed");
      if (!current()) return;
      if (!confirmed) { setStorageFailure(true); return; }
      queryClient.setQueryData(key, saved);
      void queryClient.invalidateQueries({ queryKey: ["food", "recommendations"] });
      setOpen(false); refreshReceipt(n => n + 1);
    },
    onError: (error, submission) => {
      const e = toApiError(error);
      const stopped = e.isAuth || e.isCapabilityUnavailable || (e.kind === "http" && e.status !== null && e.status >= 400 && e.status < 500 && e.code !== "IDEMPOTENCY_IN_PROGRESS");
      const stored = updateFoodPreferenceReceipt(scope, submission.idempotencyKey, stopped ? "blocked" : "unknown");
      if (current()) { if (!stored) setStorageFailure(true); refreshReceipt(n => n + 1); }
    },
  });
  type Original = { idempotencyKey: string; expectedVersion: number };
  const originalResult = useMutation({
    mutationKey: [...key, "original-result"], retry: false, networkMode: "always",
    mutationFn: async (original: Original) => {
      const before = readFoodPreferenceReceipt(scope);
      if (!current() || before.kind !== "held" || !["pending", "unknown"].includes(before.receipt.state)
          || before.receipt.idempotencyKey !== original.idempotencyKey || before.receipt.expectedVersion !== original.expectedVersion) return null;
      if (!food.preferenceResult) throw new ApiError({ kind: "capability", code: "CAPABILITY_UNAVAILABLE", retryable: false, message: "这里暂时还不能查看上次保存。" });
      const result = await food.preferenceResult(petId, subject, original.idempotencyKey);
      if (!current()) return null;
      const after = readFoodPreferenceReceipt(scope);
      if (after.kind !== "held" || after.receipt.idempotencyKey !== original.idempotencyKey || after.receipt.expectedVersion !== original.expectedVersion
          || !["pending", "unknown"].includes(after.receipt.state)) return null;
      if (!validOriginalFoodPreferenceResult(result, scope, original.expectedVersion)) throw changed();
      if (result.state === "completed") {
        if (!updateFoodPreferenceReceipt(scope, original.idempotencyKey, "completed")) { setStorageFailure(true); return null; }
        refreshReceipt(n => n + 1);
      }
      // No private result body enters the mutation cache or a current preference projection.
      return result.state;
    },
    onError: (error, original) => {
      if (!current()) return;
      const e = toApiError(error);
      if (e.isAuth || e.isCapabilityUnavailable || (e.kind === "http" && e.status !== null && e.status >= 400 && e.status < 500)) {
        if (!updateFoodPreferenceReceipt(scope, original.idempotencyKey, "blocked")) setStorageFailure(true);
        refreshReceipt(n => n + 1);
      }
    },
  });
  const readCurrent = async () => {
    if (!current()) return;
    const before = readFoodPreferenceReceipt(scope);
    setOpen(false);
    const response = await pref.refetch();
    if (!current() || before.kind !== "held" || before.receipt.state !== "completed" || response.isError || !response.data || !matches(response.data)) return;
    if (!confirmCurrentFoodPreferenceReceipt(scope, before.receipt.idempotencyKey, response.data.version)) setStorageFailure(true);
    refreshReceipt(n => n + 1);
  };
  const notice = storageFailure || receipt.kind === "error" ? "这次保存的记录暂时无法安全读取或保存，已停止保存。你仍可只读查看当前偏好。"
    : receipt.kind === "held" && receipt.receipt.state === "completed" ? "上次保存已确认。请查看当前偏好后再继续调整。"
    : held ? "上次保存的结果还没有确认，暂时不能再次保存。查看当前偏好不会确认上次保存。" : null;
  if (pref.isError || (pref.data && !matches(pref.data))) return <Card flat>{notice ? <p role="alert">{notice}</p> : null}<ErrorState error={pref.error ?? changed()} onRetry={() => { if (current()) void pref.refetch(); }} /></Card>;
  if (!pref.data || !draft) return notice ? <Card flat><p role="status">{notice}</p></Card> : null;
  const who = subject === "pet" ? "TA 的旅途口味" : "我的用餐偏好";
  const summary = DIMENSIONS.filter((d) => pref.data.taste[d.key] !== null)
    .map((d) => `${d.label}${(pref.data.taste[d.key] ?? 0) > 0 ? "+" : "−"}`)
    .join(" ");
  return (
    <Card flat className="ps-stack">
      <div className="ps-row" style={{ justifyContent: "space-between" }}>
        <div>
          <strong>{who}</strong>
          <div className="ps-muted">
            {pref.data.label}{summary ? ` · ${summary}` : ""}
          </div>
        </div>
        <Button size="sm" variant="ghost" icon={open ? "close" : "settings"} onClick={() => setOpen((v) => !v)}>
            {open ? "收起" : "调整"}
          </Button>
      </div>
      {scope.mode === "fixture" ? <DataOriginBadge origin="fixture" label="演示偏好，只在本地预览" /> : null}
      {notice ? <div className="ps-stack"><p role="status">{notice}</p><Button size="sm" variant="ghost" onClick={() => { void readCurrent(); }}>只读查看当前偏好</Button></div> : null}
      {receipt.kind === "held" && ["pending", "unknown"].includes(receipt.receipt.state) && !storageFailure ? <Button size="sm" variant="ghost" loading={originalResult.isPending} onClick={() => {
        if (current() && !originalResult.isPending) originalResult.mutate({ idempotencyKey: receipt.receipt.idempotencyKey, expectedVersion: receipt.receipt.expectedVersion });
      }}>查询原保存结果</Button> : null}
      {originalResult.data === "in_progress" ? <p role="status">上次保存还在处理中，请稍后手动查询。</p> : null}
      {originalResult.data === "unknown" ? <p role="status">暂时还查不到上次保存的结果，先不要重复保存。</p> : null}
      {originalResult.isError ? <ErrorState error={originalResult.error} /> : null}
      {save.isSuccess && !open && !blocked ? <p role="status">这份偏好已保存。</p> : null}
      {open ? (
        <form aria-label={`${who}设置`}
          className="ps-stack"
          onSubmit={(e) => {
            e.preventDefault();
            if (!current() || blocked || save.isPending || !matches(draft)) return;
            const restrictions: DietaryRestriction[] =
              subject === "owner"
                ? restrictionText
                    .split(/[、,，\s]+/)
                    .map((t) => t.trim())
                    .filter(Boolean)
                    .slice(0, 8)
                    .map((label) => ({ kind: "avoid", label, private: true }))
                : [];
            const idempotencyKey = newIdempotencyKey("food-pref");
            const reserved = reserveFoodPreferenceReceipt(scope, draft.version, idempotencyKey);
            if (reserved !== "reserved") { if (reserved === "error") setStorageFailure(true); else refreshReceipt(n => n + 1); return; }
            save.mutate({ next: { ...draft, label: draft.label.trim() || "我的口味", restrictions }, idempotencyKey, expectedVersion: draft.version });
          }}
        >
          <fieldset className="ps-stack" disabled={blocked || save.isPending} style={{ border: 0, padding: 0, margin: 0, minWidth: 0 }}>
          <div className="ps-field">
            <label htmlFor={`pref-label-${subject}`}>偏好名字</label>
            <input id={`pref-label-${subject}`} className="ps-input" maxLength={20} value={draft.label} onChange={(e) => setDraft({ ...draft, label: e.target.value })} />
          </div>
          {DIMENSIONS.map((d) => (
            <div key={d.key} className="ps-field">
              <span className="ps-muted">{d.label}</span>
              <div className="ps-segmented" role="group" aria-label={`${d.label}的偏好`}>
                {LEVELS.map((l) => (
                  <ToggleChip key={l.label} pressed={draft.taste[d.key] === l.value} onToggle={() => setDraft({ ...draft, taste: { ...draft.taste, [d.key]: l.value } })}>
                    {l.label}
                  </ToggleChip>
                ))}
              </div>
            </div>
          ))}
          {subject === "owner" ? (
            <div className="ps-field">
              <label htmlFor="pref-restrictions">不吃/过敏（用顿号分隔）</label>
              <input id="pref-restrictions" className="ps-input" value={restrictionText} onChange={(e) => setRestrictionText(e.target.value)} placeholder="比如：花生、牛肉" />
              <span className="ps-muted">
                <Icon name="lock" size={12} /> 只用于给你推荐，不会出现在朋友圈或任何公开页面。
              </span>
            </div>
          ) : (
            <Chip>这是 TA 自己的口味，不会改动你的现实用餐偏好</Chip>
          )}
          <Button type="submit" variant="primary" loading={save.isPending}>
            保存
          </Button>
          </fieldset>
          {save.isError ? <ErrorState error={save.error} /> : null}
        </form>
      ) : null}
    </Card>
  );
}
